/**
 * FooterBar - ページ下部のコントロールバー
 * ページネーション、表示件数切り替え、件数表示を提供
 */

import React, { useMemo, useEffect } from 'react';
import {
  Box,
  Group,
  Select,
  Text,
  Pagination,
  useMantineColorScheme,
  useMantineTheme,
} from '@mantine/core';
import { useFooter } from '../contexts/FooterContext';

interface FooterBarProps {
  /** フッターのタイプ（'default' | 'simple'） */
  type?: 'default' | 'simple';
  /** 現在のページ番号 (1-based) */
  currentPage?: number;
  /** 総ページ数 */
  totalPages?: number;
  /** 1ページあたりの表示件数 */
  itemsPerPage?: number;
  /** 総件数 */
  totalItems?: number;
  /** 表示中の開始位置 (1-based) */
  startItem?: number;
  /** 表示中の終了位置 */
  endItem?: number;
  /** ページ変更時のコールバック */
  onPageChange?: (page: number) => void;
  /** 表示件数変更時のコールバック */
  onItemsPerPageChange?: (items: number) => void;
  /** 表示件数の選択肢 */
  itemsPerPageOptions?: number[];
  /** フッターを表示するかどうか */
  visible?: boolean;
}

export const FooterBar: React.FC<FooterBarProps> = ({
  type: propType,
  currentPage: propCurrentPage,
  totalPages: propTotalPages,
  itemsPerPage: propItemsPerPage,
  totalItems: propTotalItems,
  startItem: propStartItem,
  endItem: propEndItem,
  onPageChange: propOnPageChange,
  onItemsPerPageChange: propOnItemsPerPageChange,
  itemsPerPageOptions: propItemsPerPageOptions = [10, 20, 50, 100],
  visible = true,
}) => {
  const { colorScheme } = useMantineColorScheme();
  const theme = useMantineTheme();
  const isDark = colorScheme === 'dark';

  // ページネーションのアクティブスタイルを動的に注入
  useEffect(() => {
    const styleId = 'footer-pagination-styles';
    let styleElement = document.getElementById(styleId);

    if (!styleElement) {
      styleElement = document.createElement('style');
      styleElement.id = styleId;
      document.head.appendChild(styleElement);
    }

    styleElement.innerHTML = `
      /* FooterBar Pagination Active State Styles */
      .mantine-Pagination-control[data-active] {
        background-color: ${isDark ? theme.colors.blue[9] : theme.colors.blue[0]} !important;
        border-color: ${isDark ? theme.colors.blue[4] : theme.colors.blue[5]} !important;
        color: ${isDark ? theme.colors.blue[0] : theme.colors.blue[7]} !important;
        font-weight: 700 !important;
        ${isDark ? `text-shadow: 0 0 10px ${theme.colors.blue[4]}40;` : ''}
      }

      .mantine-Pagination-control[data-active]:hover {
        background-color: ${isDark ? theme.colors.blue[8] : theme.colors.blue[1]} !important;
      }

      .mantine-Pagination-control:not([data-active]) {
        background-color: ${isDark ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.01)'};
        border-color: ${isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)'};
        color: ${isDark ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.7)'};
      }

      .mantine-Pagination-control:not([data-active]):hover {
        background-color: ${isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)'} !important;
        border-color: ${isDark ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.15)'} !important;
      }
    `;

    return () => {
      // クリーンアップ不要（スタイルは残す）
    };
  }, [isDark, theme.colors.blue]);
  
  // Contextから情報を取得
  const { pagination, pageType } = useFooter();
  
  // Contextからページネーション情報がある場合はdefault、なければsimple
  const type = propType || (pagination && pageType === 'list' ? 'default' : 'simple');
  const currentPage = pagination?.currentPage || propCurrentPage || 1;
  const totalPages = pagination?.totalPages || propTotalPages || 1;
  const itemsPerPage = pagination?.itemsPerPage || propItemsPerPage || 20;
  const totalItems = pagination?.totalItems || propTotalItems || 0;
  const startItem = pagination?.startItem || propStartItem || 1;
  const endItem = pagination?.endItem || propEndItem || 0;
  const onPageChange = pagination?.onPageChange || propOnPageChange;
  const onItemsPerPageChange = pagination?.onItemsPerPageChange || propOnItemsPerPageChange;
  const itemsPerPageOptions = pagination?.itemsPerPageOptions || propItemsPerPageOptions;

  if (!visible) return null;

  // 表示件数の選択肢を文字列配列に変換
  const selectData = itemsPerPageOptions.map(value => ({
    value: value.toString(),
    label: `${value}件表示`,
  }));

  // simpleタイプの場合は開発者名のみ表示
  if (type === 'simple') {
    return (
      <Box
        h={48}
        px={{ base: 'sm', md: 'lg' }}
        style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          background: isDark
            ? 'linear-gradient(135deg, rgba(26, 27, 30, 0.98) 0%, rgba(30, 41, 59, 0.95) 100%)'
            : 'linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(240, 249, 255, 0.95) 100%)',
          backdropFilter: 'blur(10px)',
          borderTop: `1px solid ${isDark
            ? 'rgba(255, 255, 255, 0.1)'
            : 'rgba(0, 0, 0, 0.1)'}`,
          zIndex: 50,
          boxShadow: `0 -20px 25px -5px rgba(0, 0, 0, 0.1), 0 -10px 10px -5px rgba(0, 0, 0, 0.04)`,  // theme.shadows.xlの上向き版
        }}
      >
        <Group h="100%" justify="center" align="center">
          {/* 中央: 開発者クレジット（刻印風） */}
          <Text
            size="xs"
            c={isDark ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.25)'}
            fw={500}
            style={{
              letterSpacing: '0.5px',
              textShadow: isDark
                ? '0 1px 2px rgba(0, 0, 0, 0.5), inset 0 -1px 1px rgba(255, 255, 255, 0.1)'
                : '0 1px 1px rgba(255, 255, 255, 0.8), inset 0 -1px 1px rgba(0, 0, 0, 0.1)',
              userSelect: 'none',
              fontFamily: 'system-ui, -apple-system, sans-serif',
            }}
          >
            Developed by D-Studio
          </Text>
        </Group>
      </Box>
    );
  }

  return (
    <Box
      h={48}
      px={{ base: 'sm', md: 'lg' }}
      style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        background: isDark
          ? 'linear-gradient(135deg, rgba(26, 27, 30, 0.98) 0%, rgba(30, 41, 59, 0.95) 100%)'
          : 'linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(240, 249, 255, 0.95) 100%)',
        backdropFilter: 'blur(10px)',
        borderTop: `1px solid ${isDark
          ? 'rgba(255, 255, 255, 0.1)'
          : 'rgba(0, 0, 0, 0.1)'}`,
        zIndex: 50,  // 統一されたz-index
        boxShadow: `0 -20px 25px -5px rgba(0, 0, 0, 0.1), 0 -10px 10px -5px rgba(0, 0, 0, 0.04)`,  // theme.shadows.xlの上向き版
      }}
    >
      <Group h="100%" justify="space-between" align="center" style={{ position: 'relative' }}>
        {/* 左側: 表示件数切り替えと件数情報 */}
        <Group gap="sm" style={{ flex: 1 }}>
          {/* 表示件数セレクトボックス (230px右に寄せる) */}
          <Box ml={{ base: 0, md: 230 }}>
            <Select
              value={itemsPerPage.toString()}
              onChange={(value) => {
                if (value && onItemsPerPageChange) {
                  onItemsPerPageChange(parseInt(value, 10));
                }
              }}
              data={selectData}
              size="xs"
              w={110}
              styles={{
                input: {
                  backgroundColor: isDark
                    ? 'rgba(255, 255, 255, 0.05)'
                    : 'rgba(0, 0, 0, 0.02)',
                  borderColor: isDark
                    ? 'rgba(255, 255, 255, 0.1)'
                    : 'rgba(0, 0, 0, 0.1)',
                },
              }}
            />
          </Box>

          {/* 件数詳細表示 */}
          <Text size="xs" c="dimmed">
            {totalItems > 0 ? (
              <>
                {startItem} - {endItem} / 全{totalItems}件
                {totalPages > 1 && ` (${totalPages}ページ中${currentPage}ページ目)`}
              </>
            ) : (
              'データがありません'
            )}
          </Text>
        </Group>

        {/* 中央: 開発者クレジット（刻印風） */}
        <Text
          size="xs"
          c={isDark ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.25)'}
          fw={500}
          style={{
            position: 'absolute',
            left: '50%',
            transform: 'translateX(-50%)',
            letterSpacing: '0.5px',
            textShadow: isDark
              ? '0 1px 2px rgba(0, 0, 0, 0.5), inset 0 -1px 1px rgba(255, 255, 255, 0.1)'
              : '0 1px 1px rgba(255, 255, 255, 0.8), inset 0 -1px 1px rgba(0, 0, 0, 0.1)',
            userSelect: 'none',
            fontFamily: 'system-ui, -apple-system, sans-serif',
          }}
        >
          Developed by D-Studio
        </Text>

        {/* 右側: ページネーション（1ページでも表示） */}
        <Pagination
          value={currentPage}
          onChange={onPageChange}
          total={totalPages}
          size="xs"
          radius="xs"
          withEdges
          siblings={1}
          boundaries={1}
        />
      </Group>
    </Box>
  );
};

export default FooterBar;